// src/contexts/FormContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { PCRForm } from '../types/formTypes';
import { useAuth } from './AuthContext';
import { createPCRForm, updatePCRForm, getPCRForm } from '../services/pcrService';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Initial empty form structure
const initialFormState: PCRForm = {
  userId: '',
  studentName: '',
  studentEmail: '',
  studentId: '',
  timestamp: new Date(),
  dispatchScenario: '',
  sceneSizeUp: {
    BSI: '',
    natureOfIllness: '',
    additionalResources: '',
    spinalPrecautions: ''
  },
  primaryAssessment: {
    generalImpression: '',
    LOCMentalStatus: {
      AVPU: '',
      orientation: []
    },
    chiefComplaint: '',
    airwayAssessment: '',
    breathingAssessment: '',
    circulationAssessment: {
      skin: {
        color: '',
        temperature: '',
        condition: ''
      },
      pulse: {
        rate: '',
        rhythm: '',
        quality: ''
      }
    },
    GCS: {
      eyeOpening: 0,
      verbalResponse: 0,
      motorResponse: 0,
      totalScore: 0
    },
    patientPriority: ''
  },
  differentialDiagnosis: {
    pathway1: '',
    pathway2: '',
    pathway3: ''
  },
  physicalExam: {
    head: {
      eyes: '',
      nose: '',
      ears: '',
      mouth: ''
    },
    neck: {
      front: '',
      sides: '',
      cervical: ''
    },
    chest: {
      inspection: '',
      auscultation: '',
      palpation: ''
    },
    abdomen: {
      inspection: '',
      palpation: ''
    },
    pelvis: {
      palpation: '',
      genitalia: ''
    },
    rightLeg: {
      upper: '',
      lower: ''
    },
    leftLeg: {
      upper: '',
      lower: ''
    },
    rightArm: {
      upper: '',
      lower: ''
    },
    leftArm: {
      upper: '',
      lower: ''
    },
    posterior: {
      thorax: '',
      lumbar: '',
      buttocks: ''
    }
  },
  hpi: {
    onset: '',
    provocation: '',
    quality: '',
    radiateRegion: '',
    severity: '',
    time: '',
    clarifyingQuestions: ''
  },
  pmh: {
    allergies: '',
    medications: '',
    pertinentPastHistory: '',
    lastOralIntake: '',
    eventsLeadingToIllness: ''
  },
  vitalSigns: {
    sets: [
      {
        time: '',
        bloodPressure: '',
        heartRate: '',
        respiratoryRate: '',
        spO2: '',
        BGL: '',
        IVGauge: null,
        EKG: {
          reading: '',
          leadType: ''
        },
        pupils: [],
        lungSounds: []
      },
      {
        time: '',
        bloodPressure: '',
        heartRate: '',
        respiratoryRate: '',
        spO2: '',
        BGL: '',
        IVGauge: null,
        EKG: {
          reading: '',
          leadType: ''
        },
        pupils: [],
        lungSounds: []
      },
      {
        time: '',
        bloodPressure: '',
        heartRate: '',
        respiratoryRate: '',
        spO2: '',
        BGL: '',
        IVGauge: null,
        EKG: {
          reading: '',
          leadType: ''
        },
        pupils: [],
        lungSounds: []
      }
    ],
    reassessment: ''
  },
  pathophysiology: {
    pathway1: '',
    pathway2: '',
    pathway3: ''
  },
  fieldDiagnosis: {
    step1: '',
    step2: '',
    step3: '',
    treatmentAndInterventions: ''
  },
  documentation: {
    narrative: '',
    radioReport: '',
    drugCards: '',
    formatAndResources: ''
  },
  lastSaved: new Date(),
  id: ''
};

interface FormContextType {
  formData: PCRForm;
  formId: string | null;
  updateFormField: (section: string, field: string, value: any) => void;
  saveForm: () => Promise<{ success: boolean }>;
  hasUnsavedChanges: boolean;
  loading: boolean;
  lastSaved: Date | null;
  isReadOnly: boolean;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

export function useForm() {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
}

export function FormProvider({ children }: { children: React.ReactNode }) {
  const location = window.location.pathname;
  const isReadOnlyMode = location.includes('/readonly');
  const { formId } = useParams<{ formId?: string }>();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState<PCRForm>(initialFormState);
  const [loading, setLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  // Load form data if formId is available
  useEffect(() => {
    async function loadForm() {
      console.log("Loading form with ID:", formId);
      
      if (formId === 'new') {
        // Handle new form creation
        console.log("Creating new form...");
        setLoading(true);
        
        if (!currentUser) {
          console.error("No current user");
          setLoading(false);
          return;
        }
        
        // Get user details if available
        let studentName = '';
        let studentEmail = '';
        let studentId = '';
        
        try {
          // Check if we have user details in the users collection
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            studentName = userData.displayName || '';
            studentEmail = userData.email || currentUser.email || '';
            studentId = userData.studentId || currentUser.uid || '';
          } else {
            // Fallback to Firebase auth user data
            studentName = currentUser.displayName || '';
            studentEmail = currentUser.email || '';
            studentId = currentUser.uid || '';
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
          // Fallback to Firebase auth user data
          studentName = currentUser.displayName || '';
          studentEmail = currentUser.email || '';
          studentId = currentUser.uid || '';
        }
        
        const newForm = {
          ...initialFormState,
          userId: currentUser.uid,
          studentName,
          studentEmail,
          studentId,
          timestamp: new Date(),
          lastSaved: new Date(),
          dispatchScenario: `At 19:41 you are dispatched to the scene of a winery in the River North neighborhood for "Altered Mental Status". Your patient is a 48 y/o female who is found sitting in a chair being tended to by the bar staff. The first thing you notice is that she is morbidly obese. As you approach, a man who states that he "called 911" and that this is "only their second date". The man then explains that they had just arrived at the restaurant and ordered a glass of wine at the standing bar (no food), while waiting to be seated. He tells you that (prior to your arrival) he had excused himself to the restroom, and when he returned, he found his date was standing there and her glass of wine was shattered on the floor. "All of a sudden she became disoriented."
You begin your assessment and she is able to tell you her name and location, but she is aphasic. You also notice that your PT has an insulin pump clipped to her belt, and your partner finds Metoprolol in her purse. When asked, the PT complains of "dizziness" and "nausea". No medic alert bracelet found. Initial vital signs are: BP 160/110, HR 134, RR 24 (shallow), skin is cool, pale, and diaphoretic, both pupils appear to have a gaze.`
        };
        
        try {
          const result = await createPCRForm(newForm);
          console.log("Form creation result:", result);
          
          if (result.success) {
            // Redirect to the proper URL with the new form ID
            window.history.replaceState(null, '', `/form/${result.id}`);
            setFormData(newForm);
            setLastSaved(new Date());
          } else {
            console.error('Failed to create new form:', result.error);
          }
        } catch (error) {
          console.error('Error creating form:', error);
        } finally {
          setLoading(false);
        }
      } else if (formId) {
        // Load existing form
        setLoading(true);
        const result = await getPCRForm(formId);
        if (result.success) {
          // Make sure we have the complete form data
          setFormData(result as PCRForm);
          if ('lastSaved' in result) {
            setLastSaved(result.lastSaved instanceof Date ? result.lastSaved : new Date(result.lastSaved));
          }
        }
        setLoading(false);
      } else if (currentUser) {
        // No formId at all (should rarely happen)
        setFormData(prevData => ({
          ...prevData,
          userId: currentUser.uid
        }));
        setLoading(false);
      }
    }

    loadForm();
  }, [formId, currentUser]);

  // Track changes to formData
  useEffect(() => {
    // Don't mark as unsaved during initial load
    if (loading) return;
    
    // If we have form data, mark as having unsaved changes
    if (formData !== initialFormState) {
      setHasUnsavedChanges(true);
    }
  }, [formData, loading]);

  // Handle beforeunload event to warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  useEffect(() => {
    // Only run this when the form is loaded and in read-only mode
    if (!loading && isReadOnlyMode) {
      // Check for section parameter in URL
      const urlParams = new URLSearchParams(window.location.search);
      const section = urlParams.get('section');
      
      if (section) {
        console.log("Section navigation requested:", section);
        // Add a small delay to ensure the DOM is fully rendered
        setTimeout(() => {
          let elementId;
          
          // Map the section parameter to the correct element ID
          switch(section) {
            case 'chief-complaint':
              elementId = 'PrimaryAssessment-chief-complaint'; // Note the uppercase P
              break;
            case 'mental-status':
              elementId = 'loc-avpu-a'; // First checkbox in the LOC section
              break;
            case 'airway-assessment':
              elementId = 'PrimaryAssessment-airway-assessment'; // Note the uppercase P
              break;
            case 'breathing-assessment':
              elementId = 'primaryAssessment-breathingAssessment';
              break;
            case 'circulation-assessment':
              elementId = 'primaryAssessment-circulationAssessment';
              break;
            case 'onset':
              elementId = 'hpi-onset';
              break;
            case 'provoke':
              elementId = 'hpi-provocation';
              break;
            case 'quality':
              elementId = 'hpi-quality';
              break;
            case 'radiate-region':
              elementId = 'hpi-radiateRegion';
              break;
            case 'severity':
              elementId = 'hpi-severity';
              break;
            case 'time':
              elementId = 'hpi-time';
              break;
            case 'allergies':
              elementId = 'pmh-allergies';
              break;
            case 'medications':
              elementId = 'pmh-medications';
              break;
            case 'pertinent-history':
              elementId = 'pmh-pertinentPastHistory';
              break;
            // Add more mappings as needed
          }
          
          console.log("Looking for element with ID:", elementId);
          
          if (elementId) {
            // Try to find the element directly
            let element = document.getElementById(elementId);
            
            // If not found by ID, try to find a label for this element
            if (!element) {
              element = document.querySelector(`label[for="${elementId}"]`);
            }
            
            // If still not found, try other selectors based on your form structure
            if (!element) {
              // Many forms use divs with data attributes
              element = document.querySelector(`[data-field="${elementId}"]`);
            }
            
            if (element) {
              console.log("Element found, scrolling to it");
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              // Highlight the element temporarily - fixed to handle null case
              const originalBackgroundColor = element.style.backgroundColor;
              element.style.backgroundColor = 'rgba(59, 130, 246, 0.2)'; // Light blue
              setTimeout(() => {
                // Check if element still exists before setting background color
                if (element) {
                  element.style.backgroundColor = originalBackgroundColor;
                }
              }, 2000);
            } else {
              console.log("Element not found in the document");
            }
          }
        }, 500);
      }
    }
  }, [loading, isReadOnlyMode]);

  // Update a specific field in the form
  const updateFormField = (section: string, field: string, value: any) => {
    setFormData(prevData => {
      // For nested fields like 'physicalExam.head.eyes'
      if (field.includes('.')) {
        const [mainField, subField, subSubField] = field.split('.');
        if (subSubField) {
          return {
            ...prevData,
            [section]: {
              ...((prevData[section as keyof PCRForm] as Record<string, any>) || {}),
              [mainField]: {
                ...(prevData[section as keyof PCRForm] as any)[mainField],
                [subField]: {
                  ...((prevData[section as keyof PCRForm] as any)[mainField][subField]),
                  [subSubField]: value
                }
              }
            }
          };
        } else {
          return {
            ...prevData,
            [section]: {
              ...((prevData[section as keyof PCRForm] as Record<string, any>) || {}),
              [mainField]: {
                ...(prevData[section as keyof PCRForm] as any)[mainField],
                [subField]: value
              }
            }
          };
        }
      } else {
        return {
          ...prevData,
          [section]: {
            ...((prevData[section as keyof PCRForm] as Record<string, any>) || {}),
            [field]: value
          }
        };
      }
    });
  };

  // Save form to Firebase
  const saveForm = async (): Promise<{ success: boolean }> => {
    if (!formId || !currentUser) return { success: false };
  
    try {
      const result = await updatePCRForm(formId, formData);
      
      if (result.success) {
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        return { success: true };
      } else {
        console.error('Error saving form:', result.error);
        return { success: false };
      }
    } catch (error) {
      console.error('Error in saveForm:', error);
      return { success: false };
    }
  };

  const value = {
    formData,
    formId: formId || null,
    updateFormField: isReadOnlyMode ? () => {} : updateFormField,
    saveForm: isReadOnlyMode ? async () => ({ success: false }) : saveForm,
    loading,
    lastSaved,
    hasUnsavedChanges: isReadOnlyMode ? false : hasUnsavedChanges,
    isReadOnly: isReadOnlyMode
  };

  return (
    <FormContext.Provider value={value}>
      {children}
    </FormContext.Provider>
  );
}